import {AggregatedStoppageStatisticsResponse, TimeStep, TimeStepForTitle} from '@domain/stoppages'
import {DateRange, timeFormatter} from '@hconnect/uikit/src/common'
import moment, {Moment} from 'moment-timezone'
import {getI18n} from 'react-i18next'

export const getTimeStep = (timeRange: DateRange<Date>, timezone: string) => {
  const from = moment.utc(timeRange.from).tz(timezone)
  const to = moment.utc(timeRange.to).tz(timezone)

  const diffInHours = to.diff(from, 'hours', true)
  const diffInDays = to.diff(from, 'days', true)
  const diffInMonths = to.diff(from, 'months', true)

  const hasDstTransition = from.isDST() !== to.isDST()

  if (diffInHours <= 24 || (hasDstTransition && diffInHours <= 25)) {
    return TimeStep.HOUR
  }
  if (diffInDays <= 7) {
    return TimeStep.DAY
  }
  if (diffInDays > 7 && diffInMonths < 2) {
    return TimeStep.WEEK
  }
  return TimeStep.MONTH
}

const isStepMonth = (
  isSameYear: boolean,
  isFirstDayOfMonth: boolean,
  isLastDayOfMonth: boolean,
  isFirstDayOfYear: boolean,
  isLastDayOfYear: boolean
) => isSameYear && isFirstDayOfMonth && isLastDayOfMonth && !(isFirstDayOfYear && isLastDayOfYear)
export const getTimeStepForTitle = (timeRange: DateRange<Date>, tz: string) => {
  const originalLocale = moment.locale()
  // using DE locale to set Monday as a first day of a week
  moment.locale('de')
  const momentFrom = moment.utc(timeRange.from).tz(tz)
  const momentTo = moment.utc(timeRange.to).tz(tz)

  const isSameHour = momentFrom.isSame(momentTo, 'hour')
  const isSameDay = momentFrom.isSame(momentTo, 'day')
  const isSameWeek = momentFrom.isSame(momentTo, 'week')
  const isSameMonth = momentFrom.isSame(momentTo, 'month')
  const isSameYear = momentFrom.isSame(momentTo, 'year')

  const startOfWeek = momentFrom.clone().startOf('week')
  const startOfMonth = momentFrom.clone().startOf('month')
  const startOfYear = momentFrom.clone().startOf('year')
  const endOfWeek = momentTo.clone().endOf('week')
  const endOfMonth = momentTo.clone().endOf('month')
  const endOfYear = momentTo.clone().endOf('year')

  const isFirstDayOfMonth = momentFrom.isSame(startOfMonth, 'day')
  const isFirstDayOfWeek = momentFrom.isSame(startOfWeek, 'day')
  const isLastDayOfWeek = momentTo.isSame(endOfWeek, 'day')
  const isLastDayOfMonth = momentTo.isSame(endOfMonth, 'day')
  const isFirstDayOfYear = momentFrom.isSame(startOfYear, 'day')
  const isLastDayOfYear = momentTo.isSame(endOfYear, 'day')
  moment.locale(originalLocale)

  if (isSameHour) {
    return TimeStepForTitle.HOUR
  }
  if (isSameDay) {
    return TimeStepForTitle.DAY
  }
  if (
    (isSameWeek && isSameYear && isSameMonth) ||
    (isSameMonth && isFirstDayOfWeek && isLastDayOfWeek)
  ) {
    return TimeStepForTitle.WEEK
  }
  if (
    isStepMonth(isSameYear, isFirstDayOfMonth, isLastDayOfMonth, isFirstDayOfYear, isLastDayOfYear)
  ) {
    return TimeStepForTitle.MONTH
  }
  if (isFirstDayOfYear && isLastDayOfYear) {
    return TimeStepForTitle.YEAR
  }
}

export const periodTimeDataIsEmpty = (data: AggregatedStoppageStatisticsResponse | undefined) => {
  return data?.every((entry) => entry.stoppagesCount === 0)
}

export const getBarTimeFormattedLabel = ({
  dateUtc,
  step,
  language,
  timezone,
  toUtc
}: {
  step: TimeStep
  dateUtc: string
  language: string
  timezone: string
  toUtc?: string
}) => {
  moment.locale(language)
  const momentDate = moment.utc(dateUtc).tz(timezone)
  const days = moment.weekdays()
  const months = moment.months()
  const day = momentDate.day()
  const month = momentDate.month()
  const year = momentDate.year()
  const week = momentDate.isoWeek()
  const monthYear = `${months[month]} ${year}`

  moment.locale('de')
  const momentFrom = moment.utc(dateUtc).tz(timezone)
  const momentTo = moment.utc(toUtc).tz(timezone)

  const isSameWeek = momentFrom.isSame(momentTo, 'week')
  const isSameMonth = momentFrom.isSame(momentTo, 'month')
  const isSameYear = momentFrom.isSame(momentTo, 'year')

  const weekTo = momentTo.isoWeek()
  const monthTo = momentTo.month()

  const isSevenDays = momentTo.diff(momentFrom, 'days') === 6
  moment.locale(language)

  switch (step) {
    case TimeStep.HOUR: {
      return timeFormatter(momentDate, language)
    }
    case TimeStep.DAY: {
      return days[day]
    }
    case TimeStep.MONTH: {
      return toUtc && isSameYear && !isSameMonth
        ? `${months[month]} - ${months[monthTo]} ${year}`
        : monthYear
    }
    case TimeStep.WEEK: {
      if (isSameWeek && isSameMonth && toUtc && !isSevenDays) {
        return getDatePeriodLabelConsistentWithOverlapingMonths(momentFrom, momentTo)
      }
      return toUtc && isSameMonth && !isSameWeek
        ? `${week} - ${weekTo} ${getI18n().t('performanceDashboard.recurringDialog.week_plural')}`
        : `${week} ${getI18n().t('performanceDashboard.recurringDialog.week')}`
    }
  }
}

const TIME_STEP_MAP: Record<TimeStepForTitle, TimeStep | null> = {
  [TimeStepForTitle.HOUR]: TimeStep.HOUR,
  [TimeStepForTitle.DAY]: TimeStep.DAY,
  [TimeStepForTitle.WEEK]: TimeStep.WEEK,
  [TimeStepForTitle.MONTH]: TimeStep.MONTH,
  [TimeStepForTitle.YEAR]: null
}
const getDatePeriodLabel = (from: Moment, to: Moment) => {
  const singleDate = from.isSame(to)
  return `${from.format('LL')}${singleDate ? '' : ` - ${to.format('LL')}`}`
}

const getDatePeriodLabelConsistentWithOverlapingMonths = (from: Moment, to: Moment) => {
  const singleDate = from.isSame(to)
  const fromEnglishFormated = from.locale('en')
  const toEnglishFormated = to.locale('en')
  return `${fromEnglishFormated.format('LL')}${
    singleDate ? '' : ` - ${toEnglishFormated.format('LL')}`
  }`
}

export const getBarTimeFormattedLabelTitle = (
  step: TimeStepForTitle | undefined,
  fromUtc: string,
  toUtc: string,
  language: string,
  timezone: string
) => {
  if (!step) {
    return getDatePeriodLabel(moment.utc(fromUtc).tz(timezone), moment.utc(toUtc).tz(timezone))
  }
  // using DE locale to set Monday as a first day of a week
  moment.locale('de')
  const momentDate = moment.utc(fromUtc).tz(timezone)
  const momentTo = moment.utc(toUtc).tz(timezone)

  const startOfWeek = momentDate.clone().startOf('week')
  const startOfMonth = momentDate.clone().startOf('month')
  const startOfYear = momentDate.clone().startOf('year')
  const endOfWeek = momentTo.clone().endOf('week')
  const endOfMonth = momentTo.clone().endOf('month')
  const endOfYear = momentTo.clone().endOf('year')

  const isFirstDayOfYear = momentDate.isSame(startOfYear, 'day')
  const isFirstDayOfMonth = momentDate.isSame(startOfMonth, 'day')
  const isFirstDayOfWeek = momentDate.isSame(startOfWeek, 'day')
  const isLastDayOfWeek = momentTo.isSame(endOfWeek, 'day')
  const isLastDayOfMonth = momentTo.isSame(endOfMonth, 'day')
  const isLastDayOfYear = momentTo.isSame(endOfYear, 'day')

  const isSingleDate = momentDate.isSame(momentTo, 'day')
  const isSingleWeek = momentDate.isSame(momentTo, 'week')
  moment.locale(language)

  if (
    !(
      (isFirstDayOfYear || isFirstDayOfMonth || isFirstDayOfWeek) &&
      (isLastDayOfYear || isLastDayOfMonth || isLastDayOfWeek)
    ) &&
    !isSingleDate &&
    !isSingleWeek
  ) {
    return getDatePeriodLabel(momentDate, momentTo)
  }
  if (step === TimeStepForTitle.YEAR) {
    const year = momentDate.year()
    const isSameYear = momentDate.isSame(momentTo, 'year')
    return isSameYear ? String(year) : `${year} - ${momentTo.year()}`
  }
  const stepForBarLabel = TIME_STEP_MAP[step]
  if (stepForBarLabel) {
    return getBarTimeFormattedLabel({
      step: stepForBarLabel,
      dateUtc: fromUtc,
      language,
      timezone,
      toUtc
    })
  }
}

export const calculateEndDate = (timeStep: TimeStep, localStartDateTime: Moment): Date => {
  switch (timeStep) {
    case TimeStep.HOUR:
      return localStartDateTime.clone().add(1, 'hour').subtract(1, 'millisecond').toDate()
    case TimeStep.DAY:
      return localStartDateTime.clone().endOf('day').toDate()
    case TimeStep.WEEK:
      return localStartDateTime.clone().endOf('week').add(1, 'day').toDate()
    case TimeStep.MONTH:
      return localStartDateTime.clone().startOf('day').endOf('month').toDate()
    default:
      throw new Error('Invalid timeStep')
  }
}
